import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Link, Navigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';
import { AuthHeroPanel } from '../components/AuthHeroPanel';
import {
  AuthErrorBanner,
  AuthSuccessBanner,
  FieldError,
  clearErrorsOnChange,
  firstFieldError,
  shouldShowFieldError,
} from '../components/fieldError';
import { CreateAccountStep } from '../components/CreateAccountStep';
import { ActiveMfaChallenge } from '../components/MfaChallenge';
import { SsoButton } from '../components/SsoButton';
import { useAuthActions } from '../hooks/useAuthActions';
import { useAuthPrefillStore } from '../hooks/useAuthPrefillStore';
import { microsoftSsoEnabled } from '../microsoftSso';
import { useLooseSearch } from '../looseSearch';
import {
  LOGIN_REASON_PARAM,
  MICROSOFT_UNLINKED_FLAG,
  PASSWORD_CHANGED_FLAG,
  SESSION_NOT_ESTABLISHED,
  parseLoginReason,
} from '../sessionNotices';
import { SessionNotEstablishedNotice } from '../components/SessionNotEstablishedNotice';
import { useUserStore } from '@/entities/user/store';
import { config } from '@/shared/config/env';

/**
 * FINALIZED UI — reviewed against the Figma export and signed off.
 *
 * Do not redesign this screen. If a change to how it LOOKS seems obviously
 * right, read the comment next to the thing first: most of what reads as odd
 * here is a decision with its reason written beside it. See
 * features/Auth/README.md before editing, and change the shared component in
 * components/AuthShell.tsx or components/fieldError.tsx rather than this file,
 * so the flow does not drift apart again.
 *
 * This applies to AI agents as much as to people.
 */
/**
 * Login page. 1:1 port of the Figma export's SignIn component,
 * `~/Downloads/BIASafe AI - Quant Model Portfolio Designer (2)/src/app/components/SignIn.tsx`,
 * specifically the `!isSignUp` branch of its Wrapper.
 *
 * Style choices that look hand-coded (raw hex values, inline fontFamily,
 * literal Tailwind arbitrary classes) are kept verbatim from the Figma
 * export so this file reads as a faithful translation rather than a
 * theme-token interpretation. Auth wiring (useAuthActions + useForm + zod
 * validation) is layered underneath the Figma visual shell.
 *
 * Two functional gaps vs the Figma export, listed so they are obvious in
 * code review and not silently lost:
 *   - Inline forgot-password OTP flow → still routes to /forgot-password
 *   - Google SSO signs in through Identity Platform's popup
 */
const FONT_SERIF = 'var(--font-brand)';
const FONT_SANS = 'var(--font-brand)';

export function LoginPage() {
  const actions = useAuthActions();
  const {
    login,
    beginGoogleSSO,
    beginMicrosoftSSO,
    error,
    credentialsRejected,
    isLoading,
    mfaChallenge,
    ssoCreate,
  } = actions;
  // Handed over by the sign-up page (an address that already has an account).
  // Read once.
  const [prefill] = useState(() => useAuthPrefillStore.getState().take());
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);
  const [showPassword, setShowPassword] = useState(false);
  // Confirmation handed over by a flow that had to sign the user out to
  // finish — see sessionNotices. Read loosely because /login declares no
  // search schema.
  const loginSearch = useLooseSearch<Record<string, unknown>>();
  const passwordJustChanged = Boolean(loginSearch[PASSWORD_CHANGED_FLAG]);
  const microsoftUnlinked = Boolean(loginSearch[MICROSOFT_UNLINKED_FLAG]);
  const sessionNotEstablished =
    parseLoginReason(loginSearch[LOGIN_REASON_PARAM]) ===
    SESSION_NOT_ESTABLISHED;

  const emailSchema = z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email');

  const passwordSchema = z
    .string()
    .min(1, 'Password is required')
    .min(8, 'Password must be at least 8 characters');

  const form = useForm({
    defaultValues: { email: prefill.email, password: '' },
    onSubmit: ({ value }) => {
      login({ email: value.email, password: value.password });
    },
  });

  // "Continue with Google" / "Continue with Microsoft" are the same flow as on
  // the sign-up page: sign in with the provider, and when there is no account
  // offer "Create your account" (the `ssoCreate` step below).

  // /dashboard, not /strategies. Three places decide where an authenticated
  // user lands and they disagreed: this one said /strategies while the login
  // handler and the `/` route both said /dashboard. So signing in rendered
  // /strategies first — firing its nine prefetches — and only then did
  // `window.location.assign('/dashboard')` hard-reload to the real landing
  // page. That double navigation is the visible "back and forth", and every
  // one of those nine requests was discarded.
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  // The password was accepted and Identity Platform is holding the session
  // until a second factor is supplied. This replaces the form rather than
  // sitting alongside it: re-submitting the password here would invalidate
  // the resolver the challenge depends on.
  if (mfaChallenge) {
    return <ActiveMfaChallenge actions={actions} />;
  }

  // No account for the provider address, and its sign-up is on.
  if (ssoCreate) return <CreateAccountStep actions={actions} />;

  return (
    <div
      /* `light`: see the note on AuthShell. LoginPage holds its own copy of
         the shell rather than using it, so it needs the pin too. */
      className="light flex items-start relative w-full min-h-screen bg-white [-webkit-font-smoothing:antialiased] [-moz-osx-font-smoothing:grayscale]"
      data-name="SignIn"
    >
      <AuthHeroPanel />

      {/* Right side — wrapper. */}
      <div
        className="flex-1 bg-white relative h-screen overflow-hidden min-w-0"
        data-name="wrapper"
      >
        {/* Logo, top-center, fixed at 48px from top. */}
        <div className="w-full flex justify-center pt-[48px] relative z-10">
          <div
            className="content-stretch flex gap-[10px] items-center justify-center leading-[0] relative shrink-0"
            data-name="logo"
          >
            <img
              src={`${config.app.basePath}biasafe-logo.png`}
              alt="BIASafe Logo"
              className="h-[38px] w-auto"
            />
          </div>
        </div>

        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 pt-16 sm:pt-20 md:pt-24 lg:pt-28 xl:pt-[120px] pb-12 sm:pb-16 md:pb-20 lg:pb-24 xl:pb-[100px]">
          <div className="w-full flex flex-col items-center">
            <div className="content-stretch flex flex-col gap-[18px] items-center relative shrink-0 w-full min-w-[192px] max-w-[256px] sm:max-w-[360px] md:max-w-[410px] lg:max-w-[460px] xl:max-w-[540px]">
              {/* Header text. */}
              <div
                className="content-stretch flex flex-col gap-[8px] items-center leading-[0] relative shrink-0 text-center w-full"
                data-name="header-text"
              >
                <div
                  className="flex flex-col font-medium justify-center relative shrink-0 text-[#121212] text-[48px] tracking-[-1.5px] w-full"
                  style={{ fontFamily: FONT_SERIF }}
                >
                  <p className="leading-[48px] text-[42px]">Welcome Back</p>
                </div>
                <div
                  className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[#3e3e3e] text-[16px] text-nowrap"
                  style={{ fontFamily: FONT_SANS }}
                >
                  <p className="leading-[24px] whitespace-pre">
                    <span className="not-italic text-[#3e3e3e]">
                      Don't have an account?
                    </span>{' '}
                    <Link
                      to="/signup"
                      className="not-italic text-[#121212] cursor-pointer hover:underline font-bold"
                    >
                      Sign Up
                    </Link>
                  </p>
                </div>
              </div>

              <div className="w-full">
                {/* Before the error banner, and dropped once there is an
                    error: "your password changed" above "that password is
                    wrong" is a contradiction to read. The newer message wins. */}
                {sessionNotEstablished && !error && (
                  <SessionNotEstablishedNotice className="mb-4" />
                )}

                {passwordJustChanged && !error && (
                  <AuthSuccessBanner className="mb-4">
                    Password changed. Sign in with your new password.
                  </AuthSuccessBanner>
                )}

                {microsoftUnlinked && !passwordJustChanged && !error && (
                  <AuthSuccessBanner className="mb-4">
                    Your email is confirmed. Sign in with a password: choose
                    Forgot password to set one.
                  </AuthSuccessBanner>
                )}

                {prefill.notice && !error && (
                  <AuthSuccessBanner className="mb-4">
                    {prefill.notice}
                  </AuthSuccessBanner>
                )}

                {error && credentialsRejected && (
                  // One message for a wrong password and an unknown address:
                  // Firebase returns the same code for both on purpose, so
                  // nothing here may say which it was. "Create an account"
                  // keeps what was typed.
                  <AuthErrorBanner className="mb-4">
                    Check your email and password, or{' '}
                    <Link
                      to="/signup"
                      className="underline"
                      onClick={() =>
                        useAuthPrefillStore
                          .getState()
                          .set({ email: form.state.values.email })
                      }
                    >
                      create an account
                    </Link>
                    .
                  </AuthErrorBanner>
                )}

                {error && !credentialsRejected && (
                  <AuthErrorBanner className="mb-4">{error}</AuthErrorBanner>
                )}

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    form.handleSubmit();
                  }}
                  className="content-stretch flex flex-col gap-[16px] items-center relative shrink-0 w-full"
                >
                  {/* Email field. */}
                  <form.Field
                    name="email"
                    validators={{ onBlur: emailSchema, onSubmit: emailSchema }}
                    listeners={{ onChange: clearErrorsOnChange }}
                  >
                    {(field) => {
                      const hasError = shouldShowFieldError(
                        field.state.meta,
                        form.state.submissionAttempts,
                      );
                      const errorText = firstFieldError(field.state.meta);
                      return (
                        <div className="content-stretch flex flex-col gap-[8px] items-start justify-center relative shrink-0 w-full">
                          <div
                            className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[#121212] text-[16px] text-center text-nowrap"
                            style={{ fontFamily: FONT_SANS }}
                          >
                            <label
                              htmlFor="email"
                              className="leading-none text-sm font-medium text-[#000000] cursor-pointer"
                            >
                              Email
                            </label>
                          </div>
                          <div
                            className={`relative rounded-md shrink-0 w-full border shadow-xs ${
                              hasError
                                ? 'border-[#ff4d4f] bg-transparent'
                                : 'border-[#121212]/20 bg-transparent focus-within:border-[#121212]'
                            }`}
                          >
                            <div className="flex flex-row items-center justify-center overflow-clip rounded-[inherit] size-full">
                              <div className="box-border content-stretch flex gap-[10px] items-center justify-center px-[12px] py-[0px] relative w-full">
                                <input
                                  id="email"
                                  aria-invalid={hasError}
                                  aria-describedby={
                                    hasError && errorText
                                      ? 'email-error'
                                      : undefined
                                  }
                                  type="email"
                                  placeholder="Enter your email"
                                  name={field.name}
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={(e) =>
                                    field.handleChange(e.target.value)
                                  }
                                  autoComplete="email"
                                  style={{ fontFamily: FONT_SANS }}
                                  className="basis-0 flex flex-col grow justify-center leading-[0] min-h-px min-w-px not-italic relative shrink-0 text-[#121212] h-9 text-base md:text-sm placeholder:text-muted-foreground bg-transparent border-none outline-none w-full"
                                />
                              </div>
                            </div>
                          </div>
                          {hasError && errorText && (
                            <FieldError id={`${field.name}-error`}>
                              {errorText}
                            </FieldError>
                          )}
                        </div>
                      );
                    }}
                  </form.Field>

                  {/* Password field. */}
                  <form.Field
                    name="password"
                    validators={{
                      onBlur: passwordSchema,
                      onSubmit: passwordSchema,
                    }}
                    listeners={{ onChange: clearErrorsOnChange }}
                  >
                    {(field) => {
                      const hasError = shouldShowFieldError(
                        field.state.meta,
                        form.state.submissionAttempts,
                      );
                      const errorText = firstFieldError(field.state.meta);
                      return (
                        <div className="content-stretch flex flex-col gap-[8px] items-start justify-center relative shrink-0 w-full">
                          <div
                            className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[#121212] text-[16px] text-center text-nowrap"
                            style={{ fontFamily: FONT_SANS }}
                          >
                            <label
                              htmlFor="password"
                              className="leading-none text-sm font-medium text-[#000000] cursor-pointer"
                            >
                              Password
                            </label>
                          </div>
                          <div
                            className={`relative rounded-md shrink-0 w-full border shadow-xs ${
                              hasError
                                ? 'border-[#ff4d4f] bg-transparent'
                                : 'border-[#121212]/20 bg-transparent focus-within:border-[#121212]'
                            }`}
                          >
                            <div className="flex flex-row items-center justify-center overflow-clip rounded-[inherit] size-full">
                              <div className="box-border content-stretch flex gap-[10px] items-center justify-center px-[12px] py-[0px] relative w-full">
                                <input
                                  id="password"
                                  aria-invalid={hasError}
                                  aria-describedby={
                                    hasError && errorText
                                      ? 'password-error'
                                      : undefined
                                  }
                                  type={showPassword ? 'text' : 'password'}
                                  placeholder="Enter your password"
                                  name={field.name}
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={(e) =>
                                    field.handleChange(e.target.value)
                                  }
                                  autoComplete="current-password"
                                  style={{ fontFamily: FONT_SANS }}
                                  className="basis-0 flex flex-col grow justify-center leading-[0] min-h-px min-w-[200px] not-italic relative shrink-0 text-[#121212] h-9 text-base md:text-sm placeholder:text-muted-foreground bg-transparent border-none outline-none"
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowPassword(!showPassword)}
                                  tabIndex={-1}
                                  className="relative shrink-0 size-[20px] cursor-pointer hover:opacity-70 transition-opacity"
                                >
                                  {showPassword ? (
                                    <EyeOff className="w-5 h-5 text-[#5B5B5B]" />
                                  ) : (
                                    <Eye className="w-5 h-5 text-[#5B5B5B]" />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                          {hasError && errorText && (
                            <FieldError id={`${field.name}-error`}>
                              {errorText}
                            </FieldError>
                          )}
                        </div>
                      );
                    }}
                  </form.Field>

                  {/* Log In button + divider + SSO buttons. */}
                  <div className="content-stretch flex flex-col gap-[18px] items-center relative shrink-0 w-full mt-2">
                    <form.Subscribe selector={(state) => [state.isSubmitting]}>
                      {([isSubmitting]) => {
                        // Always clickable — validation runs on submit (so
                        // browser-autofilled values aren't blocked by a stale
                        // canSubmit). Only block during an in-flight request.
                        const disabled = isSubmitting || isLoading;
                        return (
                          <button
                            type="submit"
                            disabled={disabled}
                            className={`relative rounded-lg shrink-0 w-full transition-colors ${
                              disabled
                                ? 'bg-[#d1d5db] cursor-not-allowed'
                                : 'bg-[#121212] hover:bg-[#2a2a2a] cursor-pointer'
                            }`}
                          >
                            <div className="flex flex-row items-center justify-center size-full">
                              <div className="box-border content-stretch flex gap-[10px] items-center justify-center px-[16px] py-[8px] relative w-full">
                                <div
                                  className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap text-white"
                                  style={{ fontFamily: FONT_SANS }}
                                >
                                  <p className="leading-[24px] whitespace-pre">
                                    {isLoading ? 'Signing in...' : 'Log In'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      }}
                    </form.Subscribe>

                    {/* Divider. */}
                    <div className="content-stretch flex gap-[16px] items-center relative shrink-0 w-full">
                      <div className="basis-0 grow h-0 min-h-px min-w-px relative shrink-0">
                        <div className="absolute bottom-0 left-0 right-0 top-[-1px]">
                          <svg
                            className="block size-full"
                            fill="none"
                            preserveAspectRatio="none"
                            viewBox="0 0 183 1"
                          >
                            <line
                              stroke="#d1d5db"
                              strokeWidth="0.5"
                              x2="183"
                              y1="0.5"
                              y2="0.5"
                            />
                          </svg>
                        </div>
                      </div>
                      <div
                        className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[#9ca3af] text-[20px] text-center text-nowrap"
                        style={{ fontFamily: FONT_SANS }}
                      >
                        <p className="leading-[20px] whitespace-pre">or</p>
                      </div>
                      <div className="basis-0 grow h-0 min-h-px min-w-px relative shrink-0">
                        <div className="absolute bottom-0 left-0 right-0 top-[-1px]">
                          <svg
                            className="block size-full"
                            fill="none"
                            preserveAspectRatio="none"
                            viewBox="0 0 183 1"
                          >
                            <line
                              stroke="#d1d5db"
                              strokeWidth="0.5"
                              x2="183"
                              y1="0.5"
                              y2="0.5"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* SSO buttons. */}
                    <div className="content-stretch flex gap-[12px] items-start relative shrink-0 w-full">
                      <SsoButton
                        onClick={beginGoogleSSO}
                        provider="google"
                        label="Continue with Google"
                      />

                      {/* Microsoft, gated on config — see microsoftSso.ts for
                          why this must not render before the provider is on. */}
                      {microsoftSsoEnabled() && (
                        <SsoButton
                          onClick={beginMicrosoftSSO}
                          provider="microsoft"
                          label="Continue with Microsoft"
                        />
                      )}
                    </div>

                    {/* Under the sign-in options, not wedged between the
                        password field and the button.
                        
                        It used to share a row with "Remember me" —
                        `justify-between` with one item on each end — and
                        removing that checkbox left it alone on a row it was
                        never designed to hold, pinned right against nothing.
                        Right-aligned under a full-width field reads as a label
                        for that field rather than as a way out of the screen.
                        
                        Here it is what it actually is: the third option, after
                        password and Google, for somebody who cannot use the
                        first one. Centred, because a centred line under two
                        centred buttons is the only thing that does not look
                        like it fell there. */}
                    <Link
                      to="/forgot-password"
                      style={{ fontFamily: FONT_SANS }}
                      className="text-[15px] leading-[20px] text-[#3d3d3d] hover:text-[#121212] transition-colors cursor-pointer mt-1"
                    >
                      Forgot Password
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Legal footer, anchored to bottom of the wrapper. */}
        <div
          className="absolute bottom-0 left-0 right-0 bg-white z-10 pb-[32px] pt-[16px] flex flex-col items-center justify-center px-4"
          style={{ fontFamily: FONT_SANS }}
        >
          <p className="leading-[22px] text-[14px] text-[#3e3e3e] text-center max-w-md">
            You agree to our{' '}
            <a
              href="https://biasafe.ai/terms-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#121212] font-medium underline cursor-pointer hover:opacity-80"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://biasafe.ai/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#121212] font-medium underline cursor-pointer hover:opacity-80"
            >
              Privacy Policy
            </a>{' '}
            when signing up and using BIASafe.
          </p>
        </div>
      </div>
    </div>
  );
}
